import React, { useCallback, useContext, useEffect, useState } from 'react';
import { Breadcrumb, Button, Checkbox } from "@boozallen-uip/uswds-react-core";
import { IndeterminateSpinner } from '@boozallen-uip/uswds-react-loading';
import { PageSubHeader, SectionContainer } from "../shared.styled";
import { UserContext } from '../UserContext';
import { NotificationContext } from './NotificationContext';
import useNotification from '../../hooks/useNotification.hook';
import NotificationItem from './NotificationItem.component';
import TableNavigation from '../../components/TableNavigation.component';
import AccessStatus from '../../components/AccessStatus.component';
import PageHeader from '../../components/PageHeader/PageHeader.component';
import {
  HOME_URL,
  NOTIFICATIONS_URL,
  ACCESS_NO_LOGIN,
  BASE_TITLE,
  PAGE_SIZE
} from '../../utlities/core.constants';

export default function NotificationsPage() {
  const [notifications, setNotifications] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [includeCleared, setIncludeCleared] = useState(false);
  const [pageSize, setPageSize] = useState(PAGE_SIZE);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [totalCount, setTotalCount] = useState(0);

  const { isLoggedIn } = useContext(UserContext);
  const { resetActiveCount } = useContext(NotificationContext);
  const { getNotifications, markAllAsRead, clearAllNotifications } = useNotification();

  useEffect(() => {
    document.title = `${BASE_TITLE} - Notifications`;
  }, []);

  const fetchNotifications = useCallback(async () => {
    setIsLoading(true);
    try {
      const response = await getNotifications(pageSize, currentPage, includeCleared);
      setNotifications(response.items || []);
      setTotalPages(response.totalPages || 0);
      setTotalCount(response.totalCount || 0);
    } catch {
      // Error handled silently - UI shows empty state
      setNotifications([]);
    } finally {
      setIsLoading(false);
    }
  }, [getNotifications, pageSize, currentPage, includeCleared]);

  useEffect(() => {
    if (isLoggedIn()) {
      fetchNotifications();
    }
  }, [isLoggedIn, fetchNotifications]);

  const handleClear = useCallback((notificationId) => {
    setNotifications(prev => prev.filter(n => n.id !== notificationId));
    setTotalCount(prev => prev - 1);
  }, []);

  const handleRead = useCallback((notificationId) => {
    setNotifications(prev =>
      prev.map(n => n.id === notificationId ? { ...n, isRead: true } : n)
    );
  }, []);

  const handleMarkAllAsRead = useCallback(async () => {
    const success = await markAllAsRead();
    if (success) {
      setNotifications(prev => prev.map(n => ({ ...n, isRead: true })));
    }
  }, [markAllAsRead]);

  const handleClearAll = useCallback(async () => {
    const success = await clearAllNotifications();
    if (success) {
      setNotifications([]);
      setTotalCount(0);
      resetActiveCount();
    }
  }, [clearAllNotifications, resetActiveCount]);

  const handlePageChange = useCallback((e) => {
    setCurrentPage(e.detail.page);
  }, []);

  const handlePageSizeChange = useCallback((newPageSize) => {
    setPageSize(newPageSize);
    setCurrentPage(1);
  }, []);

  if (!isLoggedIn()) {
    return <AccessStatus access={ACCESS_NO_LOGIN} />;
  }

  const unreadNotifications = notifications.filter(n => !n.isRead);

  return (
    <div>
      <PageHeader imageUrl="/page-banners/account_hero_2x.webp" backgroundSize='2800px 391px' className='2xl:!bg-cover'>
        Notifications
      </PageHeader>

      <SectionContainer>
        <Breadcrumb
          links={[
            { "href": HOME_URL, "text": "Home" },
            { "href": NOTIFICATIONS_URL, "text": "Notifications" }
          ]}
        />

        <div className="flex justify-between items-center mb-6">
          <PageSubHeader className='mb-0'>Your Notifications</PageSubHeader>
          <div className="flex gap-2">
            {unreadNotifications.length > 0 && (
              <Button outline onClick={handleMarkAllAsRead}>
                Mark All as Read
              </Button>
            )}
            {notifications.some(n => !n.isCleared) && (
              <Button outline onClick={handleClearAll}>
                Clear All
              </Button>
            )}
          </div>
        </div>

        <div className="mb-4">
          <Checkbox
            id="include-cleared"
            checked={includeCleared}
            onChange={(e) => {
              setIncludeCleared(e.target.checked);
              setCurrentPage(1);
            }}
          >
            Show cleared notifications
          </Checkbox>
        </div>

        <p className="text-gray-600 mb-4">
          {totalCount === 0
            ? 'No notifications'
            : `${totalCount} notification${totalCount !== 1 ? 's' : ''}`}
        </p>

        {isLoading ? (
          <div className="flex justify-center items-center py-10" role="status" aria-live="polite">
            <IndeterminateSpinner type="circle" color="blue" />
            <span className="sr-only">Loading notifications...</span>
          </div>
        ) : (
          <div aria-live="polite" aria-atomic="false">
            {notifications.length === 0 ? (
              <div className="text-center py-10 text-gray-500" role="status">
                <p>You have no notifications.</p>
              </div>
            ) : (
              <ul className="list-none p-0 m-0" aria-label="Notifications list">
                {notifications.map((notification) => (
                  <li key={notification.id}>
                    <NotificationItem
                      notification={notification}
                      onClear={handleClear}
                      onRead={handleRead}
                    />
                  </li>
                ))}
              </ul>
            )}

            {totalPages > 1 && (
              <div className="mt-6">
                <TableNavigation
                  pageSize={pageSize}
                  setPageSize={handlePageSizeChange}
                  totalPages={totalPages}
                  setPage={handlePageChange}
                  currentPage={currentPage}
                />
              </div>
            )}
          </div>
        )}
      </SectionContainer>
    </div>
  );
}
